import { appRoute as route, serviceRoute } from "./paths.js";
import { getInsightDetail, insightDetailRoute } from "./insightDetails.js";

const ACCENTS = ["#6c8cff", "#9d6cff", "#ff6c9c", "#3fbf8f", "#f5c96e", "#6ce0ff", "#fb923c", "#a78bfa"];

function scrollFrom(labels) {
  return {
    items: labels.map((label, i) => ({
      label,
      accent: ACCENTS[i % ACCENTS.length],
    })),
  };
}

function page({
  slug,
  title,
  badge = title,
  titleSegments,
  subtitle,
  tags,
  ctaPrimary = "Start a conversation",
  approachTitleSegments,
  approachSubtitle,
  steps,
  testimonialsSubtitle,
  faqsSubtitle,
  faqItems,
  insightsSubtitle,
  insightItems,
}) {
  return {
    title,
    documentTitle: `Taldel — ${title}`,
    banner: {
      badge,
      titleSegments,
      subtitle,
      ctaPrimary,
      ctaPrimaryHref: route("contact"),
      ctaSecondary: "See our approach",
      ctaSecondaryHref: serviceRoute(slug, "approach"),
      tags,
    },
    scroll: scrollFrom(tags),
    approach: {
      badge: "Approach",
      titleSegments: approachTitleSegments,
      subtitle: approachSubtitle,
      steps,
    },
    testimonials: {
      badgePre: "Trusted by teams who",
      badgeBrand: "ship with Taldel",
      titleSegments: [
        { text: "Proof from ", highlight: null },
        { text: "teams", highlight: "primary" },
        { text: " who ", highlight: null },
        { text: "shipped", highlight: "secondary" },
      ],
      subtitle: testimonialsSubtitle,
      items: [
        {
          rating: "4.9/5",
          quote:
            "Taldel felt like an extension of our own product org — clear communication, strong craft, and delivery we could trust.",
          name: "Olivia Taylor",
          role: "Founder, BlockSavvy",
          initials: "OT",
        },
        {
          rating: "5.0/5",
          quote:
            "The engineers were productive from week one. Scope stayed honest, and the quality held up under real user traffic.",
          name: "Emma Wilson",
          role: "CTO, Fintech Startup",
          initials: "EW",
        },
        {
          rating: "4.8/5",
          quote:
            "We moved faster without sacrificing standards. Reviews were sharp and the collaboration rhythm was excellent.",
          name: "Lucas Walker",
          role: "VP Engineering, SaaS Platform",
          initials: "LW",
        },
        {
          rating: "5.0/5",
          quote:
            "Serious delivery partner energy — not a black-box agency. We stayed in control of product direction the whole time.",
          name: "Sofia Martinez",
          role: "Head of Product, RetailTech",
          initials: "SM",
        },
      ],
    },
    faqs: {
      badge: "FAQs",
      titleSegments: [
        { text: "Questions ", highlight: null },
        { text: "teams", highlight: "primary" },
        { text: " ask ", highlight: null },
        { text: "first", highlight: "secondary" },
      ],
      subtitle: faqsSubtitle,
      items: faqItems,
    },
    insights: {
      badge: "Insights",
      titleSegments: [
        { text: "Ideas for ", highlight: null },
        { text: "building", highlight: "primary" },
        { text: " better", highlight: "secondary" },
      ],
      subtitle: insightsSubtitle,
      items: insightItems.map((item) => {
        const detail = item.slug ? getInsightDetail(item.slug) : null;
        return {
          ...item,
          href: insightDetailRoute(item.slug),
          accent: detail?.accent || item.accent || "#6c8cff",
        };
      }),
    },
  };
}

export const extraServicePages = {
  "ai-agentic": page({
    slug: "ai-agentic",
    title: "Agentic AI",
    titleSegments: [
      { text: "Agentic systems ", highlight: null },
      { text: "that", highlight: "primary" },
      { text: " take ", highlight: null },
      { text: "action", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Design and ship AI agents that plan, tool-call, and execute real workflows — with evals, guardrails, and production observability built in.",
    tags: ["Agent orchestration", "Tool calling", "RAG + memory", "Evals & guardrails"],
    ctaPrimary: "Talk about agents",
    approachTitleSegments: [
      { text: "From ", highlight: null },
      { text: "prompt", highlight: "primary" },
      { text: " to ", highlight: null },
      { text: "agent", highlight: "secondary" },
    ],
    approachSubtitle: "Practical agent delivery — scoped jobs-to-be-done, reliable tools, and measurable autonomy.",
    steps: [
      {
        title: "Discover",
        description: "Map the workflows an agent should own, plus success metrics, risk boundaries, and human handoff points.",
      },
      {
        title: "Assemble",
        description: "Pair AI engineers with your product context to design tools, memory, and orchestration that fit your stack.",
      },
      {
        title: "Ship",
        description: "Launch with evaluation harnesses, tracing, and controls so agents stay useful — and safe — in production.",
      },
      {
        title: "Scale",
        description: "Expand agent coverage across ops and product once quality and ROI are proven.",
      },
    ],
    testimonialsSubtitle: "Teams use Taldel to move from AI demos to agents that actually complete work.",
    faqsSubtitle: "Straight answers about how Taldel builds agentic AI systems.",
    faqItems: [
      {
        question: "What is an AI agentic engagement?",
        answer:
          "We build systems that can plan steps, call tools/APIs, and complete workflows — not just chat interfaces.",
      },
      {
        question: "Do you only prototype, or ship production agents?",
        answer:
          "Production. Prototypes are a step. We plan for evals, cost, latency, permissions, and monitoring.",
      },
      {
        question: "Can agents work with our internal tools?",
        answer:
          "Yes. We integrate your APIs, data sources, and policies so agents operate inside your environment.",
      },
      {
        question: "How do you keep agents reliable?",
        answer:
          "Evaluation suites, human-in-the-loop checkpoints, tracing, and clear fallback paths when confidence is low.",
      },
      {
        question: "Can we start with one workflow?",
        answer: "That’s preferred. Prove value on one high-ROI job, then expand autonomy carefully.",
      },
    ],
    insightsSubtitle: "Notes on shipping agents that teams adopt — not just demos they abandon.",
    insightItems: [
      {
        slug: "scope-the-job-before-the-model",
        category: "Agents",
        title: "Scope the job before the model",
        excerpt: "Why clear workflow ownership beats vague “add an agent” initiatives.",
        readTime: "4 min",
      },
      {
        slug: "evals-keep-agents-honest",
        category: "Quality",
        title: "Evals that keep agents honest",
        excerpt: "A lightweight loop for tool-calling quality, regressions, and cost control.",
        readTime: "5 min",
      },
      {
        slug: "human-handoffs-that-users-trust",
        category: "Product",
        title: "Human handoffs that users trust",
        excerpt: "Design patterns for when agents should ask, escalate, or stop.",
        readTime: "5 min",
      },
    ],
  }),

  "app-development": page({
    slug: "app-development",
    title: "App Development",
    titleSegments: [
      { text: "Apps ", highlight: null },
      { text: "built", highlight: "primary" },
      { text: " to ", highlight: null },
      { text: "scale", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Build web and mobile apps from MVP to scale — auth, workspaces, billing, analytics, and the product loops that turn first sessions into retention.",
    tags: ["Web & mobile apps", "Subscriptions", "Admin & roles", "Product analytics"],
    ctaPrimary: "Build your app",
    approachTitleSegments: [
      { text: "How we ", highlight: null },
      { text: "ship", highlight: "primary" },
      { text: " apps ", highlight: null },
      { text: "faster", highlight: "secondary" },
    ],
    approachSubtitle: "A product-minded app build — solid foundations first, growth features next.",
    steps: [
      {
        title: "Discover",
        description: "Clarify your users, core jobs-to-be-done, and the thinnest lovable app that validates retention.",
      },
      {
        title: "Assemble",
        description: "Staff full-stack and mobile engineers who’ve shipped auth, billing, and multi-platform releases before.",
      },
      {
        title: "Ship",
        description: "Deliver secure auth, core UX, web and store releases, and deploy pipelines your team can own.",
      },
      {
        title: "Scale",
        description: "Layer billing, roles, analytics, and growth surfaces as usage and revenue expand.",
      },
    ],
    testimonialsSubtitle: "Founders and product leaders use Taldel to launch apps without bloated timelines.",
    faqsSubtitle: "What to expect when Taldel builds your app.",
    faqItems: [
      {
        question: "Can you take an app from idea to MVP?",
        answer: "Yes — we scope a sharp MVP, ship a stable core, then iterate toward monetization and scale.",
      },
      {
        question: "Do you build for web and mobile?",
        answer: "Both. Responsive web apps by default, plus iOS and Android with React Native or Flutter when a native presence matters.",
      },
      {
        question: "What about billing and subscriptions?",
        answer: "We integrate common billing providers and design plan/entitlement models that match your go-to-market.",
      },
      {
        question: "Will we own the codebase?",
        answer: "Yes. Everything ships in your repos with documentation your team can extend.",
      },
      {
        question: "How quickly can we launch?",
        answer: "Depends on scope, but we optimize for a usable production MVP measured in weeks, not quarters.",
      },
    ],
    insightsSubtitle: "Short reads on app foundations, monetization, and product loops that retain.",
    insightItems: [
      {
        slug: "mvp-means-retention-not-features",
        category: "Apps",
        title: "MVP means retention, not features",
        excerpt: "How to pick the smallest product that still proves people come back.",
        readTime: "4 min",
      },
      {
        slug: "tenancy-decisions-you-shouldnt-defer",
        category: "Architecture",
        title: "Tenancy decisions you shouldn’t defer",
        excerpt: "What to lock early so growth doesn’t force a rewrite.",
        readTime: "6 min",
      },
      {
        slug: "billing-ux-that-doesnt-kill-conversion",
        category: "Growth",
        title: "Billing UX that doesn’t kill conversion",
        excerpt: "Plan, trial, and upgrade patterns that feel product-native.",
        readTime: "5 min",
      },
    ],
  }),

  "ui-ux-design": page({
    slug: "ui-ux-design",
    title: "UI/UX Design",
    titleSegments: [
      { text: "Interfaces ", highlight: null },
      { text: "users", highlight: "primary" },
      { text: " actually ", highlight: null },
      { text: "love", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Product design, UX flows, and design systems that make complex software feel simple — ready for engineering to ship without guesswork.",
    tags: ["Product UX", "Design systems", "Prototyping", "Usability research"],
    ctaPrimary: "Start a design sprint",
    approachTitleSegments: [
      { text: "Design that ", highlight: null },
      { text: "ships", highlight: "primary" },
      { text: " with ", highlight: null },
      { text: "engineering", highlight: "secondary" },
    ],
    approachSubtitle: "Research-informed UX and systems thinking — not slides that die in Figma.",
    steps: [
      {
        title: "Discover",
        description: "Understand users, jobs, and constraints through research, audits, and product workshops.",
      },
      {
        title: "Assemble",
        description: "Pair product designers with your engineering stack so components and tokens map to code.",
      },
      {
        title: "Ship",
        description: "Deliver flows, UI kits, and prototypes that are implementation-ready — with clear specs.",
      },
      {
        title: "Scale",
        description: "Grow a design system that keeps marketing, app, and admin surfaces coherent as you expand.",
      },
    ],
    testimonialsSubtitle: "Product teams use Taldel design to remove friction and accelerate frontend delivery.",
    faqsSubtitle: "How Taldel UI/UX engagements work with your product and engineering teams.",
    faqItems: [
      {
        question: "Do you only deliver mockups?",
        answer:
          "No. We deliver flows, interaction specs, and systems that engineering can implement with fewer round-trips.",
      },
      {
        question: "Can you work inside our existing product?",
        answer: "Yes — audits, redesigns, and incremental UX improvements are common starting points.",
      },
      {
        question: "Do you build design systems?",
        answer: "Yes. Foundations, components, and usage guidance that match how your frontend team ships.",
      },
      {
        question: "Will designers collaborate with engineers?",
        answer: "Closely. That collaboration is the point — less handoff waste, more shipped polish.",
      },
      {
        question: "Can you support a launch timeline?",
        answer: "We scope design to your milestones and prioritize the flows that unblock revenue or retention.",
      },
    ],
    insightsSubtitle: "On UX craft, systems, and design that survives contact with engineering.",
    insightItems: [
      {
        slug: "design-the-decision-not-the-screen",
        category: "UX",
        title: "Design the decision, not the screen",
        excerpt: "Why job-centered flows beat page inventories every time.",
        readTime: "4 min",
      },
      {
        slug: "tokens-that-frontend-teams-actually-use",
        category: "Systems",
        title: "Tokens that frontend teams actually use",
        excerpt: "Naming and structure that reduce “close enough” implementation drift.",
        readTime: "5 min",
      },
      {
        slug: "lightweight-tests-before-big-redesigns",
        category: "Research",
        title: "Lightweight tests before big redesigns",
        excerpt: "A practical research cadence for startups without a research org.",
        readTime: "5 min",
      },
    ],
  }),

  "cyber-security": page({
    slug: "cyber-security",
    title: "Cyber Security",
    titleSegments: [
      { text: "Security built ", highlight: null },
      { text: "into", highlight: "primary" },
      { text: " the ", highlight: null },
      { text: "product", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Threat detection and response built to catch what automation misses. Every incident is triaged, contained, and turned into a hardened defense.",
    tags: ["Threat modeling", "Identity", "Cloud hardening", "Detection"],
    ctaPrimary: "Talk security",
    approachTitleSegments: [
      { text: "Defend ", highlight: null },
      { text: "early", highlight: "primary" },
      { text: ", operate ", highlight: null },
      { text: "calmly", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    approachSubtitle: "Cyber security delivery that teams can run — not a slide deck of controls nobody owns.",
    steps: [
      {
        title: "Discover",
        description: "Map assets, identities, and the decisions an attacker would actually try to force.",
      },
      {
        title: "Assemble",
        description: "Staff security engineers who can sit in the product repo — not only in a separate GRC track.",
      },
      {
        title: "Ship",
        description: "Harden auth, cloud, and app surfaces with tests, logging, and a detection path that is usable.",
      },
      {
        title: "Scale",
        description: "Turn findings into a backlog the product team can execute every sprint, not a yearly scramble.",
      },
    ],
    testimonialsSubtitle: "Teams trust Taldel for security work that doesn’t freeze delivery.",
    faqsSubtitle: "Straight talk on how Taldel approaches cyber security.",
    faqItems: [
      {
        question: "Is this a pen test or an embedded security team?",
        answer:
          "Either, or both. We can run a scoped assessment, then stay to fix and operate — inside your repos and rituals.",
      },
      {
        question: "What do you typically cover?",
        answer:
          "Identity and access, cloud posture, application hardening, logging/detection, and the product decisions that create risk.",
      },
      {
        question: "Will this slow the roadmap?",
        answer:
          "The goal is the opposite: smaller, earlier controls so you don’t halt a launch for a last-minute finding.",
      },
      {
        question: "Do you work with our existing SOC or GRC tools?",
        answer: "Yes. We plug into what you already run — SIEM, IdP, cloud, ticketing — rather than forcing a rip-and-replace.",
      },
      {
        question: "Can this pair with a product squad?",
        answer:
          "That’s the default. Security engineers sit with web, SaaS, or AI teams so fixes land in the same pull requests.",
      },
    ],
    insightsSubtitle: "On shipping security that product teams will actually run.",
    insightItems: [
      {
        slug: "threat-models-before-tools",
        category: "Security",
        title: "Threat models before tools",
        excerpt: "A simple filter for what to harden first — before buying another dashboard.",
        readTime: "4 min",
      },
      {
        slug: "identity-is-the-new-perimeter",
        category: "Identity",
        title: "Identity is the new perimeter",
        excerpt: "SSO, least privilege, and session design that survive a real incident.",
        readTime: "5 min",
      },
      {
        slug: "ship-security-into-the-sprint",
        category: "Delivery",
        title: "Ship security into the sprint",
        excerpt: "How to make findings a backlog item, not a once-a-year fire drill.",
        readTime: "6 min",
      },
    ],
  }),

  "staff-augmentation": page({
    slug: "staff-augmentation",
    title: "Staff Augmentation",
    titleSegments: [
      { text: "Extend your ", highlight: null },
      { text: "team", highlight: "primary" },
      { text: " without the ", highlight: null },
      { text: "hiring drag", highlight: "secondary" },
      { text: ".", highlight: null },
    ],
    subtitle:
      "Embed AI-vetted engineers into your roadmap — same tools, clear ownership, timezone overlap, and the flexibility to scale up or down.",
    tags: ["Embedded engineers", "Squad pods", "Timezone overlap", "Flexible scale"],
    ctaPrimary: "Build your bench",
    approachTitleSegments: [
      { text: "How ", highlight: null },
      { text: "augmentation", highlight: "primary" },
      { text: " should ", highlight: null },
      { text: "feel", highlight: "secondary" },
    ],
    approachSubtitle: "Hire outcomes, not resumes — people who plug into your rituals and raise throughput.",
    steps: [
      {
        title: "Discover",
        description: "Define roles, seniority mix, stack needs, and what “good” looks like in the first 30 days.",
      },
      {
        title: "Assemble",
        description: "Match AI-vetted engineers to your culture and interview for ownership, not buzzwords.",
      },
      {
        title: "Ship",
        description: "Operate inside your repos, standups, and tools with transparent progress and code quality.",
      },
      {
        title: "Scale",
        description: "Add specialists or resize the bench as priorities shift — without restarting recruiting.",
      },
    ],
    testimonialsSubtitle: "Companies use Taldel staff augmentation to move faster without building a hiring machine.",
    faqsSubtitle: "How Taldel staff augmentation engagements work day to day.",
    faqItems: [
      {
        question: "Is this staff augmentation or a managed squad?",
        answer: "Either. Start with embedded engineers or a pod with delivery support — based on what you need.",
      },
      {
        question: "Will engineers work in our tools?",
        answer: "Yes. Slack, Jira, GitHub, Linear — your stack, your rituals.",
      },
      {
        question: "How are people vetted?",
        answer: "AI-assisted evaluation on skills, code quality, and communication before you meet them.",
      },
      {
        question: "Can we change team composition later?",
        answer: "Yes. Add specialists or resize as priorities shift.",
      },
      {
        question: "How quickly can we start?",
        answer: "Most teams begin within days after role fit is confirmed.",
      },
    ],
    insightsSubtitle: "On embedding capacity without losing product quality or culture.",
    insightItems: [
      {
        slug: "from-one-engineer-to-a-full-pod",
        category: "Teams",
        title: "From one engineer to a full pod",
        excerpt: "A practical path for growing capacity without rebuilding recruiting.",
        readTime: "5 min",
      },
      {
        slug: "make-timezone-overlap-non-negotiable",
        category: "Delivery",
        title: "Make timezone overlap non-negotiable",
        excerpt: "Why a few shared hours change everything for remote teammates.",
        readTime: "4 min",
      },
      {
        slug: "interview-for-ownership-not-keywords",
        category: "Leadership",
        title: "Interview for ownership, not keywords",
        excerpt: "What high-performing embedded engineers show in week one.",
        readTime: "6 min",
      },
    ],
  }),
};
